import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  // Stable device fingerprint stored in localStorage — used by the OTP step-up
  // flow to skip the OTP on trusted devices (still respects the 24h re-auth).
  const getDeviceId = () => {
    try {
      let d = window.localStorage.getItem("pmr_device_id");
      if (!d) {
        d = (crypto.randomUUID && crypto.randomUUID()) || `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem("pmr_device_id", d);
      }
      return d;
    } catch { return ""; }
  };

  const login = async (email, password) => {
    const device_id = getDeviceId();
    const { data } = await api.post("/auth/login", { email, password, device_id });
    // Step-up required: return the otp-required payload, leaving the user
    // unauthenticated until verifyOtpAndLogin() succeeds.
    if (data?.otp_required) {
      return { otp_required: true, reason: data.reason, mobile_hint: data.mobile_hint, user_id: data.user_id, email };
    }
    setUser(data.user);
    return data.user;
  };

  const verifyLoginOtp = async (userId, code) => {
    // Login flow: verify by user_id (returned from /auth/login response)
    // so the user doesn't have to re-enter their mobile number.
    const device_id = getDeviceId();
    const { data } = await api.post(
      "/auth/otp/verify",
      { user_id: userId, code, purpose: "login", device_id },
    );
    if (data?.verified && data?.user) {
      setUser(data.user);
      return data.user;
    }
    throw new Error("OTP verification failed");
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.pending_approval) {
      // Volunteer pending — no session
      return { pending_approval: true, user: data.user };
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLoginOtp, register, logout, refresh: fetchMe, formatApiError, getDeviceId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
